import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Flame, LogOut, LayoutGrid, Upload, Receipt, Tag, FolderOpen } from "lucide-react";
import { useTranslation } from "react-i18next";
import LanguageCurrencySelector from "./LanguageCurrencySelector";
import TrialBanner from "./TrialBanner";

export default function Layout({ children, sidebar }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <div className="min-h-screen relative z-10" data-testid="layout">
      <TrialBanner />
      <header className="border-b border-white/5 backdrop-blur-md bg-black/50 sticky top-0 z-30">
        <div className="max-w-[1600px] mx-auto px-6 py-4 flex items-center justify-between gap-4 flex-wrap">
          <Link to={user?.role === "vendor" ? "/vendor" : "/dashboard"} className="flex items-center gap-3">
            <Flame className="w-5 h-5 text-amber-500" />
            <div className="font-display text-xl">{t("common.appName")}</div>
            <span className="font-mono-data text-[9px] tracking-[0.3em] uppercase text-text-secondary hidden sm:inline">
              {user?.role === "vendor" ? t("nav.vendorPortal") : t("nav.commandCenter")}
            </span>
          </Link>
          <nav className="flex items-center gap-5 flex-wrap">
            {user?.role !== "vendor" && (
              <>
                <button onClick={() => navigate("/dashboard")} className="font-mono-data text-xs uppercase tracking-widest text-text-secondary hover:text-white inline-flex items-center gap-2" data-testid="nav-desk">
                  <LayoutGrid className="w-3.5 h-3.5" /> {t("nav.desk")}
                </button>
                <button onClick={() => navigate("/upload")} className="font-mono-data text-xs uppercase tracking-widest text-text-secondary hover:text-white inline-flex items-center gap-2" data-testid="nav-upload">
                  <Upload className="w-3.5 h-3.5" /> {t("nav.uploadRfp")}
                </button>
                <button onClick={() => navigate("/bid-bond")} className="font-mono-data text-xs uppercase tracking-widest text-text-secondary hover:text-white inline-flex items-center gap-2" data-testid="nav-bond">
                  <Receipt className="w-3.5 h-3.5" /> {t("nav.bidBonds")}
                </button>
                <button onClick={() => navigate("/library")} className="font-mono-data text-xs uppercase tracking-widest text-text-secondary hover:text-white inline-flex items-center gap-2" data-testid="nav-library">
                  <FolderOpen className="w-3.5 h-3.5" /> {t("nav.library", "Library")}
                </button>
              </>
            )}
            <button onClick={() => navigate("/pricing")} className="font-mono-data text-xs uppercase tracking-widest text-text-secondary hover:text-white inline-flex items-center gap-2" data-testid="nav-pricing">
              <Tag className="w-3.5 h-3.5" /> {t("nav.pricing")}
            </button>
            <LanguageCurrencySelector compact />
            <div className="flex items-center gap-3 pl-4 border-l border-white/10">
              {user?.picture && (
                <img src={user.picture} alt="" className="w-7 h-7 rounded-full" />
              )}
              <div className="text-right">
                <div className="text-sm inline-flex items-center gap-1.5">
                  {user?.name}
                  {user?.is_admin && (
                    <span className="font-mono-data text-[9px] tracking-widest px-1.5 py-0.5 rounded bg-amber-500 text-black" data-testid="admin-badge">
                      ADMIN
                    </span>
                  )}
                </div>
                <div className="font-mono-data text-[9px] uppercase tracking-widest text-text-secondary">{user?.company || user?.role}</div>
              </div>
              <button onClick={logout} data-testid="logout-btn" className="text-text-secondary hover:text-white">
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          </nav>
        </div>
      </header>

      <main className="max-w-[1600px] mx-auto px-6 py-8">
        {sidebar ? (
          <div className="grid lg:grid-cols-12 gap-6">
            <div className="lg:col-span-9">{children}</div>
            <aside className="lg:col-span-3 space-y-4">{sidebar}</aside>
          </div>
        ) : children}
      </main>
    </div>
  );
}
